import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getRegions, getUserData, registerUser } from "../../../API/API";
import {
  autoLogin,
  showToast,
  TOKEN_KEYS,
} from "../../../components/common/Util/Util";
import { setUser } from "../../../ReduxStore/UserSlice";
import OrganizationVerification from "./OrganizationVerification";
import { useNavigate } from "react-router-dom";

const LenderOnboardingForm = ({ initialData, onVerificationStart, onBack }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showVerification, setShowVerification] = useState(false);
  const [lenderId, setLenderId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [regions, setRegions] = useState([]);
  const user = useSelector((state) => state.user.user);
  const [formData, setFormData] = useState({
    mobileNumber: user?.MobileNumber || "",
    email: user?.Email || "",
    name: user?.Name || "",
    financialInstitutionType: "",
    financialInstitution: "",
    location: "",
    typeOfLending: "",
    typeOfRetailLoans: "",
    designation: "",
  });

  const [loading, setLoading] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const response = await getRegions();
        if (response?.result?.region) {
          setRegions(response.result.region);
        }
      } catch (error) {
        console.error("Error fetching regions:", error);
        showToast("Failed to fetch regions", "error");
      }
    };

    fetchRegions();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // check if all required fields are filled
  const isFormValid =
    !!formData.financialInstitutionType &&
    !!formData.financialInstitution &&
    !!formData.location &&
    !!formData.typeOfLending &&
    !!formData.typeOfRetailLoans &&
    !!formData.designation &&
    disclaimerAccepted;

  // API call with temporary token
  const makeApiCallWithTempToken = async (apiCall) => {
    const tempToken = localStorage.getItem("tempJwtToken");
    const originalToken = localStorage.getItem(TOKEN_KEYS.JWT);

    if (tempToken) {
      localStorage.setItem(TOKEN_KEYS.JWT, tempToken);
    }

    try {
      return await apiCall();
    } finally {
      if (originalToken) {
        localStorage.setItem(TOKEN_KEYS.JWT, originalToken);
      } else {
        localStorage.removeItem(TOKEN_KEYS.JWT);
      }
    }
  };

  const handleNext = async () => {
    if (!isFormValid) return;

    setLoading(true);
    let registrationSucceeded = false;
    try {
      const registrationData = {
        id: user?.ID,
        userType: "lender",
        name: formData.name,
        mobileNumber: formData.mobileNumber,
        email: formData.email,
        screen: "create",
        address: formData.location,
        lender: {
          financialInstitutionType: formData.financialInstitutionType,
          financialInstitution: formData.financialInstitution,
          typeOfLending: formData.typeOfLending,
          typeOfRetailLoans: formData.typeOfRetailLoans,
          designation: formData.designation,
          location: formData.location,
        },
      };

      const response = await registerUser(registrationData);
      if (response?.statusCode === 200) {
        registrationSucceeded = true;
        showToast("Registration successful", "success");
        const newLenderId = response.result.lenderId;
        const newUserId = response.result.ID;
        setLenderId(newLenderId);
        setUserId(newUserId);

        localStorage.setItem("tempJwtToken", response.result.jwtToken);
        localStorage.setItem("tempUuid", response.result.UUID);

        try {
          const userGetResponse = await makeApiCallWithTempToken(() =>
            getUserData(response.result.UUID)
          ).catch((error) => {
            console.error("Error fetching user data:", error);
            navigate("/");
          });

          if (userGetResponse?.result?.verified) {
            dispatch(setUser(response.result));
            localStorage.setItem("jwtToken", response.result.jwtToken);
            localStorage.removeItem("tempJwtToken");
            localStorage.removeItem("tempUuid");
            autoLogin(response.result, dispatch);
            window.location.href = "/dashboard";
          } else {
            setShowVerification(true);
            onVerificationStart?.(newLenderId, formData, newUserId);
          }
        } catch (error) {
          onVerificationStart?.(newLenderId, formData, newUserId);
        }
      } else {
        showToast(response?.message || "Registration failed", "error");
      }
    } catch (error) {
      console.error(error);
      if (!registrationSucceeded) {
        showToast("Registration failed", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    onBack?.();
  };

  if (showVerification) {
    return (
      <OrganizationVerification
        formData={formData}
        initialData={initialData}
        onBack={handleBack}
        lenderId={lenderId}
        userId={userId}
        preventClose={true}
      />
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 800, mx: "auto", my: 4 }}>
      <Typography variant="h5" gutterBottom color="primary" sx={{ mb: 3 }}>
        Basic Details
      </Typography>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 4 }}>
        Let's begin by entering some basic details about your business.
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
            fullWidth
            disabled
            label="Mobile Number"
            name="mobileNumber"
            value={formData.mobileNumber}
            placeholder="+91-XXXXXXXXXX"
            helperText="Enter a valid mobile number for OTP verification."
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
            label="Email Address"
            name="email"
            type="email"
            value={formData.email}
            disabled
            helperText="An OTP will be sent to verify this email ID."
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
            label={
              <span>
                Financial Institution Name{" "}
                <span style={{ color: "red" }}>*</span>
              </span>
            }
            name="financialInstitution"
            value={formData.financialInstitution}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>
              Financial Institution Type <span style={{ color: "red" }}>*</span>
            </InputLabel>
            <Select
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRadius: 2, // makes the border itself curved
                },
              }}
              name="financialInstitutionType"
              value={formData.financialInstitutionType}
              onChange={handleChange}
              label="Financial Institution Type"
            >
              <MenuItem value="bank">Bank</MenuItem>
              <MenuItem value="nbfc">NBFC</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>
              Location <span style={{ color: "red" }}>*</span>
            </InputLabel>
            <Select
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRadius: 2, // makes the border itself curved
                },
              }}
              name="location"
              value={formData.location}
              onChange={handleChange}
              label="Location"
            >
              {regions.map((region) => (
                <MenuItem key={region} value={region}>
                  {region}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>
              Type of Lending <span style={{ color: "red" }}>*</span>
            </InputLabel>
            <Select
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRadius: 2, // makes the border itself curved
                },
              }}
              name="typeOfLending"
              value={formData.typeOfLending}
              onChange={handleChange}
              label="Type of Lending"
            >
              <MenuItem value="secured">Secured</MenuItem>
              <MenuItem value="unsecured">Unsecured</MenuItem>
              <MenuItem value="both">Both</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>
              Type of Retail Loans <span style={{ color: "red" }}>*</span>
            </InputLabel>
            <Select
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRadius: 2, // makes the border itself curved
                },
              }}
              name="typeOfRetailLoans"
              value={formData.typeOfRetailLoans}
              onChange={handleChange}
              label="Type of Retail Loans"
            >
              <MenuItem value="personal">Personal Loans</MenuItem>
              <MenuItem value="business">Business Loans</MenuItem>
              <MenuItem value="both">Both</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>
              Designation <span style={{ color: "red" }}>*</span>
            </InputLabel>
            <Select
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRadius: 2, // makes the border itself curved
                },
              }}
              name="designation"
              value={formData.designation}
              onChange={handleChange}
              label="Designation"
            >
              <MenuItem value="manager">Manager</MenuItem>
              <MenuItem value="director">Director</MenuItem>
              <MenuItem value="executive">Executive</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={disclaimerAccepted}
                onChange={(e) => setDisclaimerAccepted(e.target.checked)}
              />
            }
            label={
              <Box
                sx={{
                  backgroundColor: "#f5f5f5",
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid #ddd",
                  maxHeight: 200,
                  overflowY: "auto",
                }}
              >
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ fontWeight: 600 }}
                >
                  Disclaimer:
                </Typography>
                <Typography variant="body2" gutterBottom>
                  • <strong>Accuracy of Information:</strong> You warrant that
                  all information provided is true, accurate, and complete. Any
                  misrepresentation may lead to the termination of our
                  partnership.
                </Typography>
                <Typography variant="body2" gutterBottom>
                  • <strong>Regulatory Compliance:</strong> You confirm that you
                  are a legitimate lending institution, fully compliant with all
                  applicable laws, regulations, and guidelines issued by the
                  Reserve Bank of India (RBI) or other relevant regulatory
                  bodies.
                </Typography>
                <Typography variant="body2" gutterBottom>
                  • <strong>Credit Assessment & Sanction:</strong> All credit
                  assessment, due diligence, loan sanctioning, and disbursement
                  processes remain your sole responsibility. We do not endorse
                  or guarantee the creditworthiness of any borrower.
                </Typography>
                <Typography variant="body2" gutterBottom>
                  • <strong>Lead Quality:</strong> While we strive to provide
                  high-quality leads, we do not guarantee conversion rates or the
                  successful closure of all loan applications routed through our
                  platform.
                </Typography>
                <Typography variant="body2" gutterBottom>
                  • <strong>Service Level Agreements (SLAs):</strong> You agree
                  to adhere to mutually agreed-upon SLAs regarding response
                  times, processing timelines, and communication with borrowers and
                  consultants.
                </Typography>
                <Typography variant="body2" gutterBottom>
                  • <strong>Data Security:</strong> You commit to maintaining
                  the confidentiality and security of all borrower and
                  consultant data accessed through the platform, in strict
                  accordance with our Privacy Policy and applicable data protection laws.
                </Typography>
                <Typography variant="body2">
                  • <strong>Platform Fees:</strong> You agree to the platform's
                  fee structure for lead generation or successful loan
                  disbursements, as detailed in a separate Lender Partnership
                  Agreement.
                </Typography>
              </Box>
            }
          />
        </Grid>

        <Grid item xs={12} sx={{ mt: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Button variant="outlined" color="primary" onClick={onBack}>
              Back
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleNext}
              disabled={!isFormValid || loading}
            >
              {loading ? "Submit..." : "Submit"}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default LenderOnboardingForm;
